import { useState } from "react";
import { Form } from "react-aria-components";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Notice, TextField } from "@bres/ui";
import { useT } from "../lib/i18n.tsx";
import { BrandLockup } from "./Brand.tsx";

export function Login() {
  const t = useT();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"password" | "totp">("password");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const post = (path: string, body: unknown) =>
    fetch(path, { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

  const submitPassword = async () => {
    setPending(true);
    setError(null);
    try {
      const res = await post("/v1/auth/sign-in/email", { email, password });
      const data = (await res.json().catch(() => ({}))) as { twoFactorRedirect?: boolean };
      if (!res.ok) return setError(t(res.status === 429 ? "error.rate_limited" : res.status >= 500 ? "error.server" : "login.failed"));
      if (data.twoFactorRedirect) return setStep("totp");
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch {
      setError(t("error.network"));
    } finally {
      setPending(false);
    }
  };

  const submitCode = async () => {
    setPending(true);
    setError(null);
    try {
      const res = await post("/v1/auth/two-factor/verify-totp", { code });
      if (!res.ok) return setError(t(res.status === 429 ? "error.rate_limited" : res.status >= 500 ? "error.server" : "login.totp_failed"));
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch {
      setError(t("error.network"));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="login">
      <aside className="login__art" aria-hidden>
        <span className="login__grid" />
        <div className="login__art-mark">
          <BrandLockup />
        </div>
        <div>
          <h2>{t("login.art_title")}</h2>
          <p>{t("login.art_lead")}</p>
        </div>
      </aside>
      <main className="login__main">
        <div className="login__card">
          <div className="login__brand">
            <h1>{step === "password" ? t("login.title") : t("login.totp_title")}</h1>
            {step === "password" ? <p>{t("login.lead")}</p> : null}
          </div>
          <Form
            className="login__panel"
            onSubmit={(e) => {
              e.preventDefault();
              void (step === "password" ? submitPassword() : submitCode());
            }}
          >
            {error ? (
              <Notice tone="danger" role="alert">
                {error}
              </Notice>
            ) : null}
            {step === "password" ? (
              <>
                <TextField label={t("login.email")} type="email" autoComplete="username" value={email} onChange={setEmail} isRequired autoFocus errorMessage={t("common.required")} />
                <TextField label={t("login.password")} type="password" autoComplete="current-password" value={password} onChange={setPassword} isRequired errorMessage={t("common.required")} />
              </>
            ) : (
              <TextField label={t("login.totp_code")} inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} isRequired autoFocus maxLength={6} />
            )}
            <Button type="submit" variant="primary" block isPending={pending}>
              {step === "password" ? t("login.submit") : t("login.totp_submit")}
            </Button>
          </Form>
          {import.meta.env.DEV ? <p className="login__demo">{t("login.demo")}</p> : null}
        </div>
      </main>
    </div>
  );
}
